import { cn } from "@ndd/ui";

// The appointment workspace's look under the always theme (NO-799; the
// approved spec is the NO-799 Linear comment "Approved design spec (Justin,
// 2026-10-22)", with the shared decisions on NO-791). Every class here is
// either a brand: utility on a plain element, or, on an @ndd/ui component that
// takes a className, a utility that reads a value only [data-theme="always"]
// defines with the classic value as its fallback (apps/always-ai/AGENTS.md).
// Markup that exists only for the theme starts `hidden` and is shown by a
// brand: display utility. With the theme off the workspace renders exactly as
// before. The theme-only values sit in the "Appointment workspace" block of
// client/src/styles.css. A leaf module, so `tsx --test` reads it.

/**
 * Wraps the classic Back link, an @ndd/ui Button: `display: contents` leaves
 * the classic layout as it was, and under the theme the link gives way to
 * the name row's quiet "Appointments" link.
 */
export const CLASSIC_BACK_SLOT = "contents brand:hidden";

/** The classic breadcrumb above the name; the theme drops it. */
export const CLASSIC_BREADCRUMB =
  "mt-3 flex items-center gap-1.5 font-sans text-caption font-extrabold uppercase tracking-[0.08em] text-subtle brand:hidden";

/** The name row: classic the title and the recap chip; under the theme an avatar leads it. */
export const NAME_ROW =
  "flex flex-wrap items-center gap-2 brand:flex-nowrap brand:gap-3.5";

/** The patient's name (the page's h1); under the theme it may wrap beside the link. */
export const NAME_TITLE =
  "font-sans text-display font-bold leading-tight tracking-[-0.015em] text-foreground brand:min-w-0";

/** The patient's 44px lime avatar, drawn only under the theme. */
export const NAME_AVATAR =
  "hidden size-11 shrink-0 place-items-center rounded-full font-sans text-body font-bold brand:grid brand:bg-(--color-avatar) brand:text-(color:--color-avatar-foreground)";

/** "← Appointments": a quiet underlined link at the right of the name row, theme only. */
export const HOME_LINK =
  "ml-auto hidden shrink-0 self-center whitespace-nowrap text-body-sm font-semibold text-primary underline decoration-1 underline-offset-3 hover:text-primary-hover brand:inline";

/**
 * The visit line. Classic it is the date line and the next-visit chip; under
 * the theme it is a white strip with a hairline and an 18px radius.
 */
export const VISIT_STRIP =
  "mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 brand:mt-[18px] brand:items-start brand:gap-x-12 brand:gap-y-3 brand:rounded-[18px] brand:border brand:border-border brand:bg-card brand:px-5 brand:py-3.5";

/** A classic part of the visit line, hidden under the theme. */
export const CLASSIC_ONLY = "brand:hidden";

/** One fact on the visit strip (THIS VISIT, NEXT VISIT), theme only. */
export const VISIT_FACT = "hidden flex-col gap-1 leading-tight brand:flex";

/** A fact's value; `missing` is the red "Not booked". */
export function visitValueClass(missing: boolean): string {
  return cn(
    "font-sans text-reading font-bold",
    missing ? "text-destructive" : "text-foreground"
  );
}

/**
 * The tab row: classic the segmented control and the notes toggle; under the
 * theme it sits on a 1px hairline that anchors the editor card.
 */
export const TAB_ROW =
  "flex flex-wrap items-center justify-between gap-2 brand:items-end brand:border-b brand:border-border brand:pb-2";

/**
 * The Documentation tabs (TabsList variant underline). The variant already
 * falls back to the classic segmented control; the classic 2px gap and the
 * theme's drop onto the hairline read theme-only values.
 */
export const TAB_LIST =
  "gap-(--tabs-underline-gap,0.125rem) mb-(--workspace-tabs-drop,0px)";

/**
 * A Documentation tab. Classic its own segmented-control look as before; the
 * variant supplies the radius, padding and the active fill (classic
 * `--background`, which is sand-50), and the size, the active shadow and the
 * active weight read theme-only values.
 */
export const TAB_TRIGGER =
  "h-full gap-1.5 text-(length:--workspace-tab-size,var(--text-body-lg)) text-muted-foreground shadow-none transition-colors duration-150 data-[state=active]:text-foreground data-[state=active]:shadow-(--tabs-underline-active-shadow,0_1px_1px_rgba(40,3,15,0.1)) data-[state=active]:font-(weight:--workspace-tab-active-weight,500) motion-reduce:transition-none";

/** The draft tab's sparkle: the underline tabs carry no icon under the theme. */
export const TAB_ICON_CLASSIC_ONLY = "brand:hidden";

/**
 * The Previous notes toggle and the reader's Widen button (Button variant
 * utility). Classic they keep their own look: a card fill and the strong
 * edge at rest, and the sand fill with a dark edge while open or pressed.
 * Under the theme they are the utility button, card-white with an olive
 * inset while open or pressed.
 */
export const UTILITY_REST =
  "border-(color:--button-utility-border,var(--border-strong)) bg-(--button-utility-bg,var(--card))";

export const UTILITY_ENGAGED_FILL =
  "bg-(--button-utility-bg,var(--accent)) hover:bg-(--workspace-engaged-hover,var(--border-strong))";

/** The open toggle's edge: aria-expanded, which the variant also styles. */
export const TOGGLE_OPEN_EDGE =
  "aria-expanded:border-(color:--button-selected-border,var(--color-muted-strong)) hover:border-(color:--button-selected-border,var(--color-muted-strong))";

/** The pressed Widen button's edge: aria-pressed, which the variant also styles. */
export const WIDEN_PRESSED_EDGE =
  "aria-pressed:border-(color:--button-selected-border,var(--color-muted-strong)) hover:border-(color:--button-selected-border,var(--color-muted-strong))";

/** A utility button's label: semibold under the theme, as the variant's own text is. */
export const UTILITY_LABEL = "brand:font-semibold";

/** A card title (the Patient quick note): display type, 17px/700 under the theme. */
export const CARD_TITLE = "brand:text-title-sm brand:font-bold";

/**
 * The strip under the editor text (EditorFooter): classic fills it with the
 * page background; under the theme it stays card-white, so the bar reads as
 * part of the editor card rather than the cream page (Justin's QA on #1010).
 */
export const EDITOR_FOOTER_FILL = "brand:bg-card";
